import { AreaKey } from '../rows-data/types';
import { useContext } from 'react';
import { FinalTableDataContext } from './FinalTableDataProvider';
import { getFinalTableKey } from './utils';
import { FinalTableEvent, FinalTableEventType } from './FinalTableProps';
import { CheckIdsProps, useCheckIds } from '../rows-data/useCheckIds';
import { useRowsDataWatch } from '../rows-data/useRowsData';

export type FinalTableDataUseProps = {
  /**
   * Přenačtení dat ze serveru - stejné jako přefiltrování (stisk tlačítka "Vyhledat").
   */
  refetch: () => void;
  /**
   * Invalidace dat pro načtené řádky, nepřefiltrovává, jen donačte aktuální data ze serveru.
   * Přenačítá data pro:
   * * datové řádky
   * * řádky reprezentující skupinu (když je seskupeno)
   * * patičku
   */
  invalidateAll: () => void;
  /**
   * Vložení nových řádků do tabulky - předávají se jen id konkrétních datových řádků pro přidání.
   * Pokud je tabulka zobrazena jako seskupená, nic se nestane (ne má to smysl).
   * Řádky se přidají tak, aby je uživatel viděl.
   * @param ids seznam id řádků
   */
  insertRows: (ids: any[]) => void;
  /**
   * Invalidace dat pro řádky reprezentující skupinu (když je seskupeno).
   * @param ids sezanm id skupin, pokud není uvedeno, invaliduje všechny skupiny
   */
  invalidateGroupRows: (ids?: any[]) => void;
  /**
   * Invalidace dat pro datové řádky.
   * @param ids seznam id řádků, pokud není uvedeno, invaliduje všechny řádky
   */
  invalidateDataRows: (ids?: any[]) => void;
  /**
   * Invalidace patičky.
   */
  invalidateFooter: () => void;
  /**
   * Informace a práce se zaškrtnutými řádky.
   */
  checkIds: CheckIdsProps<any>;
};

export type FinalClientTableDataUseProps = {
  /**
   * Informace a práce se zaškrtnutými řádky.
   */
  checkIds: CheckIdsProps<any>;
};

export type FinalTableDataUseConfig = {
  /**
   * Zatím se nepoužívá. Asi bude v budoucnu implementováno a mělo by dělat to, že to samo pozná co všechno se má invalidovat.
   */
  autoInvalidate?: boolean;
};

export const useFinalClientTableData = (
  filterKey: AreaKey
): FinalClientTableDataUseProps => {
  const finalTableDataContext = useContext(FinalTableDataContext);
  if (!finalTableDataContext.initByProvider) {
    throw new Error('Chybí FinalTableDataProvider, obalte jím aplikaci');
  }

  const rowsData = useRowsDataWatch(filterKey);

  const checkIds = useCheckIds<any>(
    undefined,
    rowsData.rows.checkedIds,
    rowsData.rows.setCheckedIds
  );

  return {
    checkIds,
  };
};

export const useFinalTableData = (
  filterKey: AreaKey,
  config?: FinalTableDataUseConfig
): FinalTableDataUseProps => {
  const finalTableDataContext = useContext(FinalTableDataContext);
  if (!finalTableDataContext.initByProvider) {
    throw new Error('Chybí FinalTableDataProvider, obalte jím aplikaci');
  }

  const key = getFinalTableKey(filterKey);
  const autoInvalidate =
    config?.autoInvalidate === undefined ? true : config?.autoInvalidate;

  const rowsData = useRowsDataWatch(filterKey);

  const checkIds = useCheckIds<any>(
    undefined,
    rowsData.rows.checkedIds,
    rowsData.rows.setCheckedIds
  );

  return {
    checkIds,
    refetch: () => {
      const event: FinalTableEvent = {
        type: FinalTableEventType.REFETCH,
        autoInvalidate,
      };
      finalTableDataContext?.eventEmitter?.emit(key, event);
    },
    invalidateFooter: () => {
      const event: FinalTableEvent = {
        type: FinalTableEventType.INVALIDATE_FOOTER,
        autoInvalidate,
      };
      finalTableDataContext?.eventEmitter?.emit(key, event);
    },
    insertRows: (ids?: any[]) => {
      const event: FinalTableEvent = {
        type: FinalTableEventType.INSERT_ROWS,
        ids: ids,
        autoInvalidate,
      };
      finalTableDataContext?.eventEmitter?.emit(key, event);
    },
    invalidateDataRows: (ids?: any[]) => {
      const event: FinalTableEvent = {
        type: FinalTableEventType.INVALIDATE_DATA_ROWS,
        ids: ids,
        autoInvalidate,
      };
      finalTableDataContext?.eventEmitter?.emit(key, event);
    },
    invalidateGroupRows: (ids?: any[]) => {
      const event: FinalTableEvent = {
        type: FinalTableEventType.INVALIDATE_GROUP_ROWS,
        ids: ids,
        autoInvalidate,
      };
      finalTableDataContext?.eventEmitter?.emit(key, event);
    },
    invalidateAll: () => {
      const event: FinalTableEvent = {
        type: FinalTableEventType.INVALIDATE_ALL,
        autoInvalidate,
      };
      finalTableDataContext?.eventEmitter?.emit(key, event);
    },
  };
};
